import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { apiCall } from '../utils/apiService';
import { sanitizeEmail, sanitizeName, sanitizeCompany, sanitizeOTP } from '../utils/inputSanitization';
import logger from '../utils/logger';

const AuthContext = createContext();

const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS', 
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTRATION_SUCCESS: 'REGISTRATION_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_OFFLINE: 'SET_OFFLINE'
};

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,  // ✅ Start as true to prevent flash
  error: null,
  errorObject: null,
  isOffline: false
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return { ...state, loading: true, error: null };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload?.user || null,
        isAuthenticated: true,
        loading: false,
        error: null
      };

    case AUTH_ACTIONS.REGISTRATION_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        isAuthenticated: false,
        user: null
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload?.error || 'An error occurred',
        errorObject: action.payload?.errorObject || null
      };

    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState, loading: false };  // ✅ Set loading false after logout

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload?.user || null,
        isAuthenticated: true,
        loading: false  // ✅ Set loading false after user set
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null, errorObject: null };

    case AUTH_ACTIONS.SET_OFFLINE:
      return { ...state, isOffline: action.payload };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [onlineStatus, setOnlineStatus] = useState(navigator.onLine);
  
  // Get current pathname from window.location (works outside Router context)
  const getCurrentPathname = () => {
    return window.location.pathname;
  };

  // ✅ Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      logger.debug('Network status: ONLINE');
      setOnlineStatus(true);
      dispatch({ type: AUTH_ACTIONS.SET_OFFLINE, payload: false });
      
      // ✅ When coming back online, verify auth
      if (state.isAuthenticated) {
        logger.debug('Back online - verifying auth');
        verifyAuth();
      }
    };

    const handleOffline = () => {
      logger.debug('Network status: OFFLINE');
      setOnlineStatus(false);
      dispatch({ type: AUTH_ACTIONS.SET_OFFLINE, payload: true });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [state.isAuthenticated]);

  // ✅ FIXED: Verify auth on app start with offline detection
  const verifyAuth = async () => {
    // ✅ Check if we're on a public route (skip auth verification)
    const currentPath = getCurrentPathname();
    const publicRoutes = ['/', '/auth', '/email-verification', '/forgot-password'];
    const isPublicRoute = publicRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
    
    if (isPublicRoute) {
      logger.debug('Skipping auth verification on public route:', currentPath);
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      return;
    }
    
    // ✅ Skip verification if offline
    if (!navigator.onLine) {
      // Try to get cached user from localStorage as fallback
      const cachedUser = localStorage.getItem('cached_user');
      if (cachedUser) {
        try {
          const user = JSON.parse(cachedUser);
          logger.debug('Using cached user data (offline mode)');
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: { user }
          });
          dispatch({ type: AUTH_ACTIONS.SET_OFFLINE, payload: true });
          return;
        } catch (e) {
          logger.error('Failed to parse cached user:', e);
        }
      }
      // If no cached user, just finish loading without auth
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      return;
    }
    
    // ✅ CRITICAL FIX: HttpOnly cookies cannot be read via JavaScript
    // We cannot check for the cookie using document.cookie
    // Instead, always try to verify by calling the endpoint
    // The backend will check the HttpOnly cookie automatically via withCredentials
    
    // If we have cached user data, try to verify it
    // If not, still try to verify (maybe cookie exists but no cache)
    const cachedUser = localStorage.getItem('cached_user');
    
    try {
      const result = await apiCall('GET', '/auth/verify-token');

      // ✅ Handle offline error
      if (result.isOffline) {
        // If offline but we have cached user, use it
        if (cachedUser) {
          try {
            const user = JSON.parse(cachedUser);
            logger.debug('Using cached user data (offline mode)');
            dispatch({
              type: AUTH_ACTIONS.SET_USER,
              payload: { user }
            });
            dispatch({ type: AUTH_ACTIONS.SET_OFFLINE, payload: true });
            return;
          } catch (e) {
            logger.error('Failed to parse cached user:', e);
          }
        }
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        dispatch({ type: AUTH_ACTIONS.SET_OFFLINE, payload: true });
        return;
      }

      if (result.success && result.data) {
        // ✅ Cache user data for offline use
        localStorage.setItem('cached_user', JSON.stringify(result.data));
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: { user: result.data }
        });
        logger.debug('✅ Auth verified successfully - user authenticated');
      } else {
        // No valid session - clear cache and logout
        logger.debug('No valid session - clearing cache and logging out');
        localStorage.removeItem('cached_user');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    } catch (error) {
      // ✅ Handle 401 errors (expected for unauthenticated users)
      if (error.response?.status === 401) {
        logger.debug('Authentication check: No valid session (expected for unauthenticated users)');
        localStorage.removeItem('cached_user');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } else {
        // Only log unexpected errors
        logger.error('Auth verification failed:', error);
        // For network errors, try to use cached user if available
        if (cachedUser && !error.response) {
          try {
            const user = JSON.parse(cachedUser);
            logger.debug('Using cached user data due to network error');
            dispatch({
              type: AUTH_ACTIONS.SET_USER,
              payload: { user }
            });
            return;
          } catch (e) {
            logger.error('Failed to parse cached user:', e);
          }
        }
        localStorage.removeItem('cached_user');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    }
  };

  // ✅ Run verification on mount, but skip for public routes
  useEffect(() => {
    const currentPath = getCurrentPathname();
    const publicRoutes = ['/', '/auth', '/email-verification', '/forgot-password'];
    const isPublicRoute = publicRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
    
    // Skip auth verification on public routes to avoid unnecessary 401 errors
    if (isPublicRoute) {
      logger.debug('Skipping auth verification on public route:', currentPath);
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      return;
    }
    
    verifyAuth();
  }, []); // Only run once on mount

  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const sanitizedEmail = sanitizeEmail(email);
      logger.debug('Login attempt:', sanitizedEmail);
      
      const result = await apiCall('POST', '/auth/login', {
        email: sanitizedEmail,
        password
      });

      // ✅ Handle offline error
      if (result.isOffline) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: 'No internet connection. Please try again when online.' }
        });
        return { success: false, error: 'No internet connection' };
      }

      if (result.success) {
        // Extract user data from response
        let userData = result.data?.data?.token?.user 
          || result.data?.token?.user 
          || result.data?.user 
          || result.user;
        
        if (!userData) {
          throw new Error('Login response missing user data');
        }
        
        // ✅ Check consultant approval BEFORE setting auth state
        if (userData.role === 'consultant' && userData.consultant_status !== 'approved') {
          logger.debug('Consultant not approved:', userData.consultant_status);
          dispatch({
            type: AUTH_ACTIONS.LOGIN_FAILURE,
            payload: { error: 'Your consultant account is pending approval' }
          });
          return { 
            success: false, 
            consultant_status: userData.consultant_status,
            error: 'Consultant account pending approval'
          };
        }
        
        // ✅ Cache user data for offline use
        localStorage.setItem('cached_user', JSON.stringify(userData));
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: userData }
        });

        return { success: true, data: result.data, user: userData };
      } else {
        // Extract error message - handle case where error might be a stringified object
        let errorMessage = result.error || 'Login failed';
        let errorObject = result.errorObject;
        
        // If error is a string that looks like an object, try to extract the message
        if (typeof errorMessage === 'string' && (errorMessage.includes("'error'") || errorMessage.includes('"error"'))) {
          try {
            // Try to parse as JSON (with double quotes)
            const jsonString = errorMessage.replace(/'/g, '"');
            const parsed = JSON.parse(jsonString);
            if (parsed && typeof parsed === 'object' && parsed.message) {
              errorMessage = parsed.message;
              errorObject = parsed;
            }
          } catch (e) {
            // If parsing fails, try regex to extract message
            const messageMatch = errorMessage.match(/['"]message['"]\s*:\s*['"]([^'"]+)['"]/);
            if (messageMatch) {
              errorMessage = messageMatch[1];
              errorObject = errorObject || { message: messageMatch[1] };
            }
          }
        }
        
        // If no errorObject but we have error, create one
        if (!errorObject && errorMessage) {
          errorObject = { message: errorMessage };
        }
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { 
            error: errorMessage,
            errorObject: errorObject
          }
        });
        return { 
          success: false, 
          error: errorMessage, 
          errorObject: errorObject,
          data: result.data 
        };
      }
    } catch (error) {
      logger.error('❌ Login error:', error);
      
      // Try to extract error from response data
      let errorMessage = 'Login failed';
      let errorObject = null;
      
      if (error.response?.data) {
        const responseData = error.response.data;
        
        // Check for detail field (FastAPI format)
        if (responseData.detail) {
          if (typeof responseData.detail === 'object' && !Array.isArray(responseData.detail)) {
            errorObject = responseData.detail;
            errorMessage = responseData.detail.message || errorMessage;
          } else if (typeof responseData.detail === 'string') {
            // Try to parse string detail
            try {
              const parsed = JSON.parse(responseData.detail.replace(/'/g, '"'));
              if (parsed && typeof parsed === 'object' && parsed.message) {
                errorObject = parsed;
                errorMessage = parsed.message;
              } else {
                errorMessage = responseData.detail;
              }
            } catch (e) {
              errorMessage = responseData.detail;
            }
          }
        } else if (responseData.message) {
          errorMessage = responseData.message;
          if (responseData.error) {
            errorObject = {
              error: responseData.error,
              message: responseData.message
            };
          }
        }
      }
      
      // Fallback to error message if no error object found
      if (!errorObject && errorMessage) {
        errorObject = { message: errorMessage };
      }
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { 
          error: errorMessage,
          errorObject: errorObject
        }
      });
      return { 
        success: false, 
        error: errorMessage,
        errorObject: errorObject
      };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const sanitizedData = {
        name: sanitizeName(userData.name),
        email: sanitizeEmail(userData.email),
        password: userData.password,
        role: userData.role,
        company: sanitizeCompany(userData.company || '')
      };
      
      const result = await apiCall('POST', '/auth/register', sanitizedData);

      // ✅ Handle offline error
      if (result.isOffline) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: 'No internet connection. Please try again when online.' }
        });
        return { success: false, error: 'No internet connection' };
      }

      if (result.success) {
        dispatch({ 
          type: AUTH_ACTIONS.REGISTRATION_SUCCESS,
          payload: { email: sanitizedData.email }
        });
        
        return { 
          success: true, 
          requires_verification: true,
          email: sanitizedData.email,
          is_consultant: sanitizedData.role === 'consultant',
          consultant_status: sanitizedData.role === 'consultant' ? 'pending' : null
        };
      } else {
        let errorMessage = 'Registration failed';
        
        // Handle validation errors from backend (422 status)
        if (result.errors && Array.isArray(result.errors)) {
          // Format field-specific validation errors
          errorMessage = result.errors
            .map(err => {
              // Extract field name (e.g., "body -> password" -> "Password")
              const fieldName = err.field ? err.field.split(' -> ').pop() : 'Field';
              const capitalizedField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
              return `${capitalizedField}: ${err.message}`;
            })
            .join('\n');
        } else if (result.error) {
          errorMessage = Array.isArray(result.error) 
            ? result.error.map(e => e.msg || e).join(', ')
            : result.error;
        }
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: errorMessage }
        });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: error.message || 'Registration failed' }
      });
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    // Clear cached user data
    localStorage.removeItem('cached_user');
    
    // Immediately clear state for instant UI update (optimistic update)
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    
    // Then call API to clear server-side session (only if online)
    if (navigator.onLine) {
      try {
        await apiCall('POST', '/auth/logout');
      } catch (error) {
        // Only log unexpected errors (401 is expected after logout)
        if (error.response?.status !== 401) {
          logger.error('Logout API error:', error);
        }
        // State already cleared, so no need to retry
      }
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const verifyEmail = async (email, otpCode) => {
    try {
      const result = await apiCall('POST', '/auth/verify-email', { 
        email: sanitizeEmail(email), 
        otp_code: sanitizeOTP(otpCode)
      });

      // ✅ Handle offline error
      if (result.isOffline) {
        return { success: false, error: 'No internet connection' };
      }

      if (result.success) {
        let userData = result.data?.data?.token?.user 
          || result.data?.token?.user 
          || result.data?.user;
        
        if (userData) {
          // ✅ Check consultant approval
          if (userData.role === 'consultant' && userData.consultant_status !== 'approved') {
            return { 
              success: true, 
              auto_login: false,
              consultant_status: userData.consultant_status,
              requires_approval: true
            };
          }
          
          // ✅ Cache user data
          localStorage.setItem('cached_user', JSON.stringify(userData));
          
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user: userData }
          });
          
          return { success: true, auto_login: true, user: userData };
        }
        
        return { success: true, auto_login: false };
      }
      
      return { success: false, error: 'Verification failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resendVerificationOTP = async (email) => {
    const result = await apiCall('POST', '/auth/resend-otp', { email });
    if (!result.success) throw new Error(result.error);
    return result;
  };

  const sendForgotPasswordOTP = async (email) => {
    const result = await apiCall('POST', '/auth/forgot-password', { email });
    if (!result.success) throw new Error(result.error);
    return result;
  };

  const resetPassword = async (email, otpCode, newPassword) => {
    const result = await apiCall('POST', '/auth/reset-password', {
      email, otp_code: otpCode, new_password: newPassword
    });
    if (!result.success) throw new Error(result.error);
    return result;
  };

  const checkPasswordStrength = async (password) => {
    try {
      const result = await apiCall('POST', '/auth/check-password-strength', { password });
      return result.success ? result.data : null;
    } catch {
      return null;
    }
  };

  const hasRole = (role) => state.user?.role === role;
  const hasAnyRole = (roles) => roles.includes(state.user?.role);

  const value = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,
    errorObject: state.errorObject,
    isOffline: state.isOffline || !onlineStatus,
    login,
    register,
    logout,
    clearError,
    verifyEmail,
    resendVerificationOTP,
    sendForgotPasswordOTP,
    resetPassword,
    checkPasswordStrength,
    hasRole,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
